import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import {
  Middleware,
  Placement,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import { twMerge } from 'tailwind-merge';

export interface CustomTooltipProps {
  children: React.ReactNode;
  title: string | ReactNode;
  placement?: Placement;
  middleware?: Middleware[];
  className?: string;
  wrapperClassName?: string;
  // Overrides the floating layer's default `z-10` (e.g. to sit above a topbar).
  floatingClassName?: string;
  strategy?: 'absolute' | 'fixed';
  withPortal?: boolean;
}

export const Tooltip: React.FC<CustomTooltipProps> = ({
  children,
  title,
  placement = 'top',
  middleware = [offset(4), flip(), shift()],
  className,
  wrapperClassName,
  floatingClassName,
  strategy = 'absolute',
  withPortal,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [animate, setAnimate] = useState(false);

  const closingRef = useRef(false);
  const raf1 = useRef<number | null>(null);
  const raf2 = useRef<number | null>(null);

  const {
    refs,
    floatingStyles,
    placement: resolvedPlacement,
  } = useFloating({
    placement,
    middleware,
    strategy,
    whileElementsMounted: autoUpdate,
  });

  const cancelRAFs = () => {
    if (raf1.current) cancelAnimationFrame(raf1.current);
    if (raf2.current) cancelAnimationFrame(raf2.current);
    raf1.current = raf2.current = null;
  };

  const handleMouseEnter = useCallback(() => {
    closingRef.current = false;
    cancelRAFs();

    setIsOpen(true);
    setReady(false);
    setAnimate(false);

    raf1.current = requestAnimationFrame(() => {
      setReady(true);
      raf2.current = requestAnimationFrame(() => {
        setAnimate(true);
      });
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    closingRef.current = true;
    cancelRAFs();
    setAnimate(false);
  }, []);

  const onTransitionEnd: React.TransitionEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.target !== e.currentTarget) return;
      if (!animate && closingRef.current) {
        setIsOpen(false);
        closingRef.current = false;
      }
    },
    [animate],
  );

  const show = ready && animate;

  const content = useMemo(
    () => (
      <div
        ref={refs.setFloating}
        style={{ ...floatingStyles, position: strategy }}
        aria-hidden={!isOpen}
        className={twMerge('z-10 pointer-events-none', floatingClassName)}
      >
        <div
          onTransitionEnd={onTransitionEnd}
          className={twMerge(
            'transition-[opacity,transform] will-change-[opacity,transform]',
            'bg-v2blueGray-800 text-white rounded-[10px] border border-v2blueGray-700',
            'shadow-[0_4px_8px_#23384133] px-3 py-1 text-xs tracking-[-0.144px] leading-[18px]',
            show ? 'opacity-100 scale-100' : 'opacity-0 scale-95',
            className,
          )}
          style={{
            transformOrigin: originConvert(resolvedPlacement),
            transitionDuration: '180ms',
          }}
        >
          {title}
        </div>
      </div>
    ),
    [
      className,
      floatingClassName,
      floatingStyles,
      isOpen,
      onTransitionEnd,
      refs,
      resolvedPlacement,
      show,
      strategy,
      title,
    ],
  );
  return (
    <div
      ref={refs.setReference}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={wrapperClassName}
    >
      {children}

      {isOpen && title && withPortal && createPortal(content, document.body)}
      {isOpen && title && !withPortal && content}
    </div>
  );
};

const originConvert = (placement: Placement) => {
  switch (placement) {
    case 'top':
      return 'bottom center';
    case 'top-start':
      return 'bottom left';
    case 'top-end':
      return 'bottom right';
    case 'bottom':
      return 'top center';
    case 'bottom-start':
      return 'top left';
    case 'bottom-end':
      return 'top right';
    case 'left':
      return 'center right';
    case 'left-start':
      return 'top right';
    case 'left-end':
      return 'bottom right';
    case 'right':
      return 'center left';
    case 'right-start':
      return 'top left';
    case 'right-end':
      return 'bottom left';
    default:
      return 'center';
  }
};
